import { COMPANY_NAME, EFFECTIVE_DATE, LEGAL_CONTACT_EMAIL } from '../config/legal'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ShieldCheck, Eye, Cookie, Database, Lock, UserX } from 'lucide-react'
import BackgroundBeams from '@/components/aceternity/BackgroundBeams'
import ScrollReveal from '@/components/aceternity/ScrollReveal'

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <BackgroundBeams />
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-10 md:pt-24 md:pb-16 text-center">
          <ScrollReveal className="flex flex-col items-center gap-4">
            <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 md:w-8 md:h-8" />
            </div>
            <Badge variant="secondary" className="bg-primary/15 text-primary border-primary/20">Legal</Badge>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
              Effective Date: {EFFECTIVE_DATE}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <Separator className="bg-border/60" />

      {/* Introduction */}
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Your Privacy Matters</CardTitle>
              <CardDescription>
                {COMPANY_NAME} ("Kinora", "we", "us") • Privacy Contact: {LEGAL_CONTACT_EMAIL}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm md:text-base text-muted-foreground">
              <p>
                This Privacy Policy explains how we collect, use, and protect your personal information when you use
                the Kinora platform.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* What We Collect */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" /> What We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Information you provide</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Email address</li>
                  <li>Username / display name</li>
                  <li>Date of birth (for age/eligibility and safety controls)</li>
                  <li>Phone number (for account security and recovery, if enabled)</li>
                  <li>Subscription status and purchase records (payment details processed by Stripe; Kinora does not store full card numbers)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">Automatically collected information</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>IP address, device identifiers, browser/OS info</li>
                  <li>Session and security signals (fraud/abuse prevention)</li>
                  <li>Usage data (e.g., pages viewed, playback events, allocations)</li>
                  <li>Approximate location derived from IP (country/region level)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* How We Use Information */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" /> How We Use Information
              </CardTitle>
              <CardDescription>We use your information to:</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Create and manage accounts, authenticate sign-in (including Google/Apple)</li>
                <li>Provide streaming playback and platform functionality</li>
                <li>Run subscriptions, billing, and account entitlements</li>
                <li>Detect and prevent abuse, fraud, and security incidents</li>
                <li>Operate analytics that are necessary to deliver the service reliably (e.g., performance, error diagnosis)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Cookies */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-primary" /> Cookies and Storage
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>Kinora uses strictly necessary cookies and/or local storage for:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Login sessions and authentication</li>
                <li>Security protections (e.g., bot prevention, fraud signals)</li>
                <li>Essential site functionality</li>
              </ul>
              <p className="pt-2">
                <strong>We do not use third-party advertising cookies at this time.</strong>
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Sharing */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Sharing and Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-4">
              <p className="font-semibold text-foreground">We do not sell personal data.</p>

              <div>
                <p className="mb-2">We share information only as needed with service providers that help us run Kinora, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Supabase</strong> (authentication, database, and related infrastructure)</li>
                  <li><strong>Cloudflare</strong> (CDN, security, performance, and edge delivery)</li>
                  <li><strong>Mux</strong> (video streaming and playback telemetry required to deliver video reliably)</li>
                  <li><strong>Stripe</strong> (payments and subscription billing)</li>
                </ul>
              </div>

              <div>
                <p className="mb-2">We may also disclose information:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>If required by law or valid legal process</li>
                  <li>To protect rights, safety, and security of users, Kinora, or the public</li>
                  <li>In connection with a merger, acquisition, or asset sale (with appropriate safeguards)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Data Retention & Deletion */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Database className="w-5 h-5 text-primary" /> Data Retention
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>We keep personal data only as long as necessary for:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Operating the Service</li>
                  <li>Security and abuse prevention</li>
                  <li>Legal and accounting compliance (purchase records may be retained longer even after account deletion)</li>
                </ul>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserX className="w-5 h-5 text-primary" /> Account Deletion
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  You can delete your account self-serve in the Service (when available). Deletion typically removes
                  or de-identifies your profile data, but we may retain limited records where required (e.g.,
                  payment/accounting, fraud prevention, legal compliance).
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Your Rights */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Your Rights</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p>
                Depending on your jurisdiction, you may have rights to access, correct, delete, or object to certain
                processing. Contact <strong>{LEGAL_CONTACT_EMAIL}</strong> to request help.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Security & Children */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Lock className="w-5 h-5 text-primary" /> Security
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  We use reasonable technical and organizational measures to protect data. No system is perfectly secure,
                  and you use the Service at your own risk.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShieldCheck className="w-5 h-5 text-primary" /> Children
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  Kinora is not intended for children under 13. If we learn we collected data from a child under 13,
                  we will delete it.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Future Features */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Future Advertising and Personalization</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p>
                Kinora does not run third-party ads today. Kinora may introduce ads and/or personalized recommendations
                in the future. If we do, we will update this policy and provide appropriate controls where required.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Changes */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p>
                We may update this Privacy Policy. If changes are material, we will provide notice via the Service.
                Continued use means you accept the updated policy.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>
    </div>
  )
}

export default Privacy
